import { useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  AppState,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  useColorScheme,
} from "react-native";

import { API_URL, DEPLOYMENT_ENVIRONMENT } from "~/lib/constants";

const PROBE_TIMEOUT_MS = 8000;
const POLL_UP_MS = 60_000;
const POLL_DOWN_MS = 10_000;
// Consecutive failed probes before the overlay appears, so one dropped request
// never flashes it.
const FAILURES_BEFORE_OVERLAY = 2;

/** True when the API answers; false on network error, timeout or 5xx. */
async function probeBackend(): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const response = await fetch(`${API_URL}/v1/health`, {
      signal: controller.signal,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Full-screen notice shown whenever the API can't be reached,
 * signed in or not. Mounted at the root, independent of auth and routing.
 */
export function BackendStatusOverlay() {
  const dark = useColorScheme() === "dark";
  const [unavailable, setUnavailable] = useState(false);
  const [checking, setChecking] = useState(false);
  const recheck = useRef<() => void>(() => {});
  const enabled = DEPLOYMENT_ENVIRONMENT !== "local" && Boolean(API_URL);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let failures = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const check = async () => {
      clearTimeout(timer);
      setChecking(true);
      const ok = await probeBackend();
      if (cancelled) return;
      setChecking(false);
      failures = ok ? 0 : failures + 1;
      setUnavailable(failures >= FAILURES_BEFORE_OVERLAY);
      timer = setTimeout(check, failures > 0 ? POLL_DOWN_MS : POLL_UP_MS);
    };

    recheck.current = () => void check();
    void check();
    const appState = AppState.addEventListener("change", (state) => {
      if (state === "active") void check();
    });
    const onOnline = () => void check();
    if (Platform.OS === "web") window.addEventListener("online", onOnline);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      appState.remove();
      if (Platform.OS === "web") window.removeEventListener("online", onOnline);
    };
  }, [enabled]);

  if (!enabled || !unavailable) return null;

  const palette = dark
    ? {
        card: "#1c1c1e",
        text: "#f5f5f5",
        muted: "#a1a1a6",
        button: "#f5f5f5",
        buttonText: "#111111",
      }
    : {
        card: "#ffffff",
        text: "#111111",
        muted: "#5c5c61",
        button: "#111111",
        buttonText: "#ffffff",
      };

  return (
    <View
      style={styles.scrim}
      accessibilityViewIsModal
      accessibilityLiveRegion="assertive"
      testID="backend-unavailable-overlay"
    >
      <View style={[styles.card, { backgroundColor: palette.card }]}>
        <Text
          accessibilityRole="header"
          style={[styles.title, { color: palette.text }]}
        >
          Can&apos;t reach the server
        </Text>
        <Text style={[styles.body, { color: palette.muted }]}>
          The service is unavailable right now. Check your connection — this
          screen goes away on its own as soon as it&apos;s back.
        </Text>
        <Pressable
          accessibilityRole="button"
          disabled={checking}
          onPress={() => recheck.current()}
          style={[
            styles.button,
            { backgroundColor: palette.button, opacity: checking ? 0.6 : 1 },
          ]}
        >
          {checking ? (
            <ActivityIndicator color={palette.buttonText} />
          ) : (
            <Text style={[styles.buttonText, { color: palette.buttonText }]}>
              Try again
            </Text>
          )}
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  scrim: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 99999,
    elevation: 99999,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    backgroundColor: "rgba(0,0,0,0.6)",
  },
  card: {
    width: "100%",
    maxWidth: 420,
    borderRadius: 16,
    padding: 24,
    gap: 12,
  },
  title: { fontSize: 20, fontWeight: "700" },
  body: { fontSize: 15, lineHeight: 22 },
  button: {
    marginTop: 8,
    minHeight: 44,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
  },
  buttonText: { fontSize: 16, fontWeight: "600" },
});
